import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { determineFilterSetName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedStringValuePrimaryFilterFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineFilterStoreScope } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedEventStudyGroupFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getPrimaryFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import React, { useMemo, useState } from 'react';
import { FilterMenuDualPanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterMenuDualPanelItemProps } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterListBoxItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { SearchableFilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/SearchableFilterListBox';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { buildIntentionallyNaiveDependencyGraph } from 'libs/end-to-end/src/utils/databases/filter-dependency-graph';
import { determineOptionsAvailable } from 'libs/end-to-end/src/utils/databases/filter-dependency-graph';
import { sortFilterValues } from 'libs/end-to-end/src/sorting';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { isBlankString } from 'libs/end-to-end/src/generic';

export const EventStudyGroupMenu = ({
  allDimensions,
  getAllLevelsForDimension,
  secondaryFilterDefinitions,
  store,
  feature,
  focusArea,
  databaseRevisionId,
  databaseId,
  organizationId,
  closeMenu,
  setPrimaryFilters,
  myObservation,
  analysisName,
}: {
  allDimensions: DimensionDefinition[];
  getAllLevelsForDimension: UseDimensionConfigPayload['getAllLevelsForDimension'];
  secondaryFilterDefinitions: FilterDefinitionWithValues[];
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  myObservation: MyObservation | null;
  analysisName: string | undefined;
}) => {
  const language = useLanguage();

  const persistedEventStudyGroup = useMemo(
    () =>
      getSelectedEventStudyGroupFromStore({
        store,
        feature,
        databaseId,
        databaseRevisionId,
        organizationId,
      }),
    [databaseId, databaseRevisionId, feature, organizationId, store],
  );

  const persistedGroupBy = useMemo(
    () =>
      getSelectedStringValuePrimaryFilterFromStore({
        store,
        feature,
        focusArea,
        analysisName,
        databaseId,
        databaseRevisionId,
        organizationId,
        primaryFilterKey: 'groupBy',
      }),
    [
      databaseId,
      databaseRevisionId,
      feature,
      focusArea,
      analysisName,
      organizationId,
      store,
    ],
  );

  const [selectedDimensionOrFilterName, setSelectedDimensionOrFilterName] =
    useState<string>(
      persistedEventStudyGroup?.dimensionOrFilterName ?? persistedGroupBy ?? '',
    );

  // eslint-disable-next-line complexity
  const items = useMemo(() => {
    const fullKey = getPrimaryFilterFullKey({
      primaryFilterKey: 'eventStudyGroup',
    });
    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (!scope) {
      return [];
    }

    const allItems: FilterMenuDualPanelItemProps[] = [];

    for (const dimension of allDimensions) {
      const levels = getAllLevelsForDimension(dimension.columnHeaderValue);

      const dimensionItems: FilterListBoxItem[] = levels.map((level) => ({
        value: level.columnValue,
        translatedContent: language.levelDisplayName({
          definition: level,
        }),
        selected:
          persistedEventStudyGroup?.dimensionOrFilterName ===
            dimension.columnHeaderValue &&
          persistedEventStudyGroup?.dimensionOrFilterValue ===
            level.columnValue,
      }));

      allItems.push({
        key: dimension.columnHeaderValue,
        translatedContent: language.dimensionDisplayName({
          definition: dimension,
        }),
        onClick: () => {
          setSelectedDimensionOrFilterName(dimension.columnHeaderValue);
        },
        content: (
          <SearchableFilterListBox
            items={dimensionItems}
            multiple={false}
            onChange={(newItems) => {
              if (newItems.length <= 0) {
                return;
              }

              setPrimaryFilters({
                selections: [
                  {
                    primaryFilterKey: 'eventStudyGroup',
                    value: [dimension.columnHeaderValue, newItems[0]],
                  },
                  {
                    primaryFilterKey: 'groupBy',
                    value: dimension.columnHeaderValue,
                  },
                ],
                shouldPreventAutoSubmit: true,
              });

              closeMenu();
            }}
          />
        ),
        selected: selectedDimensionOrFilterName === dimension.columnHeaderValue,
        itemValue: dimension.columnHeaderValue,
      });
    }

    const filterSetName = determineFilterSetName({
      feature,
      focusAreas: myObservation?.focusAreas ?? [],
      focusArea,
      analysisName,
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

    for (const secondaryFilterDefinition of secondaryFilterDefinitions) {
      const displayType =
        secondaryFilterDefinition.displayType ??
        FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE;
      // These are special filter types that have no business being a group select for trends.
      if (displayType !== 'select_single' && displayType !== 'select_multi') {
        continue;
      }

      const availableOptions = determineOptionsAvailable({
        header: secondaryFilterDefinition.columnHeaderValue,
        graph: buildIntentionallyNaiveDependencyGraph(
          secondaryFilterDefinitions,
        ),
        fallbackUniqueValues: secondaryFilterDefinition.uniqueValues,
        inputs: {},
        uniqueColumnMap:
          myObservation?.uniqueValuesMapPerSet.find(
            (x) => x.filterSetName === filterSetName,
          )?.uniqueValuesMap ?? [],
      });

      const filterItems: FilterListBoxItem[] = sortFilterValues(
        availableOptions,
      ).map((filterOptionValue) => ({
        value: filterOptionValue,
        translatedContent: filterOptionValue,
        selected:
          persistedEventStudyGroup?.dimensionOrFilterName ===
            secondaryFilterDefinition.columnHeaderValue &&
          persistedEventStudyGroup?.dimensionOrFilterValue ===
            filterOptionValue,
      }));

      allItems.push({
        key: secondaryFilterDefinition.columnHeaderValue,
        translatedContent: language.dangerousUserInput({
          value: secondaryFilterDefinition.displayName,
        }),
        onClick: () => {
          setSelectedDimensionOrFilterName(
            secondaryFilterDefinition.columnHeaderValue,
          );
        },
        selected:
          selectedDimensionOrFilterName ===
          secondaryFilterDefinition.columnHeaderValue,
        itemValue: secondaryFilterDefinition.columnHeaderValue,
        content: (
          <SearchableFilterListBox
            items={filterItems}
            multiple={false}
            onChange={(newItems) => {
              if (newItems.length <= 0) {
                return;
              }

              setPrimaryFilters({
                selections: [
                  {
                    primaryFilterKey: 'eventStudyGroup',
                    value: [
                      secondaryFilterDefinition.columnHeaderValue,
                      newItems[0],
                    ],
                  },
                  {
                    primaryFilterKey: 'groupBy',
                    value: secondaryFilterDefinition.columnHeaderValue,
                  },
                ],
                shouldPreventAutoSubmit: true,
              });

              closeMenu();
            }}
          />
        ),
      });
    }
    return allItems.filter((item) => !isBlankString(item.key));
  }, [
    feature,
    databaseId,
    databaseRevisionId,
    organizationId,
    focusArea,
    myObservation?.focusAreas,
    myObservation?.filterSetDefinitionsWithValues,
    myObservation?.uniqueValuesMapPerSet,
    myObservation?.pipelineAnalysisConfiguration,
    analysisName,
    allDimensions,
    getAllLevelsForDimension,
    language,
    selectedDimensionOrFilterName,
    persistedEventStudyGroup?.dimensionOrFilterName,
    persistedEventStudyGroup?.dimensionOrFilterValue,
    setPrimaryFilters,
    closeMenu,
    secondaryFilterDefinitions,
  ]);
  return <FilterMenuDualPanel items={items} bottomProps={'none'} />;
};
